import type {ReactElement} from 'react';
import {useMemo} from 'react';
import React, {useEffect, useRef} from 'react';
import {NavLeftOptions} from '@Components/poster-editor/components/poster-editor-nav-bar/components/nav-left-options';
import {NavRightOptions} from '@Components/poster-editor/components/poster-editor-nav-bar/components/nav-right-options';
import BaseNavOption from '@Components/poster-editor/components/poster-editor-nav-bar/components/base-nav-option/base-nav-option';
import type {ContainerQueryObject} from '@Hooks/useContainerQuery';
import {useContainerQuery} from '@Hooks/useContainerQuery';
import {GlobalPosterEditorJqueryElement} from '@Components/poster-editor/poster-editor.types';
import {LogoDisplayOptions} from '@Components/poster-editor/components/poster-editor-nav-bar/components/nav-left-options/nav-left-options.types';
import {PosterEditingNavbarMode} from '@Components/poster-editor/components/poster-editor-nav-bar/poster-editor-nav-bar.types';
import {NavRightSideSkeletonSize} from '@Components/poster-editor/components/poster-editor-nav-bar/components/nav-right-options-skeleton/nav-right-options-skeleton.types';
import {NavRightSideOptionTypes} from '@Components/poster-editor/components/poster-editor-nav-bar/components/nav-right-options/nav-right-options.types';
import {Type} from '@Components/button';
import {IconType} from '@Components/icon-v2/icon.types';
import {isSuperUserLoggedInAsAnotherUser, isUserStudent} from '@Libraries/user.library';
import {closeVersionHistoryPanel} from '@Components/poster-editor/library/poster-editor-library';
import styles from './poster-editor-nav-bar.module.scss';
import {useAppSelector} from '@/hooks';

const navBarContainerQueryObject: ContainerQueryObject = {
  xxsmall: {
    minWidth: 0,
    maxWidth: 359,
  },
  xsmall: {
    minWidth: 359,
    maxWidth: 660,
  },
  smaller: {
    minWidth: 359,
    maxWidth: 550,
  },
  small: {
    minWidth: 660,
    maxWidth: 900,
  },
  medium: {
    minWidth: 900,
    maxWidth: 1040,
  },
  large: {
    minWidth: 1040,
    maxWidth: 1160,
  },
  xlarge: {
    minWidth: 1160,
    maxWidth: 1248,
  },
  xxlarge: {
    minWidth: 1248,
  },
};

interface PosterEditorNavBarProps {
  mode?: PosterEditingNavbarMode;
}

export function PosterEditorNavBar({mode = PosterEditingNavbarMode.DEFAULT}: PosterEditorNavBarProps): ReactElement<any> {
  const containerRef = useRef<HTMLElement>(null);
  const params = useContainerQuery(containerRef, navBarContainerQueryObject);

  useEffect(() => {
    if (containerRef.current && window.posterEditor) {
      window.posterEditor.elements[GlobalPosterEditorJqueryElement.POSTER_EDITOR_NAVBAR] = $(containerRef.current);
    }
    return () => {
      if (window.posterEditor) {
        window.posterEditor.elements[GlobalPosterEditorJqueryElement.POSTER_EDITOR_NAVBAR] = undefined;
      }
    };
  }, []);

  const hasUnsavedChanges = useAppSelector((state) => {
    return state.posterEditor.hasUnsavedChanges;
  });

  const showBackButton = (): boolean => {
    return !(params.xxsmall || params.xsmall);
  };

  const showLogo = (): LogoDisplayOptions => {
    if (params.xxsmall) {
      return LogoDisplayOptions.HIDDEN;
    }

    if (params.smaller || params.medium) {
      return LogoDisplayOptions.SHORT;
    }

    if (params.large || params.xlarge || params.xxlarge) {
      return LogoDisplayOptions.FULL;
    }

    return LogoDisplayOptions.SHORT;
  };

  const showFileDropdown = (): boolean => {
    return params.small || params.medium || params.large || params.xlarge || params.xxlarge;
  };

  const getSaveOptionDisplayType = (): NavRightSideOptionTypes => {
    if (params.xxlarge || params.xlarge || (mode === PosterEditingNavbarMode.EMBEDDED && (params.medium || params.large || params.small))) {
      return NavRightSideOptionTypes.BUTTON;
    }
    return NavRightSideOptionTypes.ICON;
  };

  const showResizeOption = (): boolean => {
    return !params.xxsmall;
  };

  const getDownloadOptionDisplayTypeForEmbedded = (): NavRightSideOptionTypes => {
    if (params.xxlarge || params.xlarge || params.large || params.medium) {
      return NavRightSideOptionTypes.BUTTON;
    }
    return NavRightSideOptionTypes.ICON;
  };

  const getCollaborationAndDownloadOptionDisplayType = (isForDownload = false): NavRightSideOptionTypes => {
    if (isUserStudent() && !isForDownload) {
      return NavRightSideOptionTypes.HIDDEN;
    }

    if (params.xxlarge || params.xlarge || params.large || params.medium) {
      return NavRightSideOptionTypes.BUTTON;
    }
    if (params.small) {
      return NavRightSideOptionTypes.ICON;
    }
    if (isForDownload) {
      return NavRightSideOptionTypes.ICON;
    }
    return NavRightSideOptionTypes.HIDDEN;
  };

  const getPublishOptionDisplayType = (): NavRightSideOptionTypes => {
    if (isUserStudent()) {
      return NavRightSideOptionTypes.HIDDEN;
    }

    if (params.xxlarge || params.xlarge || params.large || params.medium) {
      return NavRightSideOptionTypes.BUTTON;
    }

    return NavRightSideOptionTypes.ICON;
  };

  const getUpgradeOptionType = (): NavRightSideOptionTypes => {
    if (isUserStudent()) {
      return NavRightSideOptionTypes.HIDDEN;
    }

    if (params.xxlarge) {
      return NavRightSideOptionTypes.BUTTON;
    }

    if (params.xxsmall || params.xsmall) {
      return NavRightSideOptionTypes.HIDDEN;
    }
    return NavRightSideOptionTypes.ICON;
  };

  const showMoreOptionDropdown = (): boolean => {
    return params.xxsmall || params.xsmall;
  };

  const showAutoSaveText = (): boolean => {
    return params.large || params.xlarge || params.xxlarge;
  };

  const getSkeletonSize = (): NavRightSideSkeletonSize => {
    if (params.large || params.xlarge || params.xxlarge) {
      return NavRightSideSkeletonSize.LARGE;
    }

    if (params.medium) {
      return NavRightSideSkeletonSize.MEDIUM;
    }

    return NavRightSideSkeletonSize.SMALL;
  };

  const getDefaultRightSideOptions = (): ReactElement<any> => {
    return (
      <NavRightOptions
        showHelpOption={params.xxlarge}
        saveOptionType={getSaveOptionDisplayType()}
        collaborateOptionType={getCollaborationAndDownloadOptionDisplayType()}
        downloadOptionType={getCollaborationAndDownloadOptionDisplayType(true)}
        publishOptionType={getPublishOptionDisplayType()}
        showMoreOptionsDropdown={showMoreOptionDropdown()}
        showAutoSaveText={showAutoSaveText()}
        openExportPanelOnPublishOrDownload={showMoreOptionDropdown()}
        upgradeOptionType={getUpgradeOptionType()}
        skeletonSize={getSkeletonSize()}
      />
    );
  };

  const getRegenRightSideOptions = (): ReactElement<any> => {
    return (
      <BaseNavOption
        id="nav-save-option"
        icon="icon-save"
        text={window.i18next.t('pmwjs_save_recreate_order')}
        optionType={params.xxsmall || params.xsmall ? NavRightSideOptionTypes.ICON : NavRightSideOptionTypes.BUTTON}
        onClickHandler={(): void => {
          void window.posterEditor?.whiteboard?.savePoster.save();
        }}
        hasMarginRight
        disabled={!hasUnsavedChanges}
      />
    );
  };

  const getRightOptionsWhenInEmailMaker = (): ReactElement<any> => {
    return (
      <NavRightOptions
        showHelpOption={params.xxlarge || params.xlarge || params.large || params.medium}
        saveOptionType={getSaveOptionDisplayType()}
        collaborateOptionType={NavRightSideOptionTypes.HIDDEN}
        downloadOptionType={getDownloadOptionDisplayTypeForEmbedded()}
        publishOptionType={NavRightSideOptionTypes.HIDDEN}
        upgradeOptionType={getUpgradeOptionType()}
        showAutoSaveText={showAutoSaveText()}
        skeletonSize={getSkeletonSize()}
      />
    );
  };

  const getVersionHistoryRightSideOptions = (): ReactElement<any> => {
    return (
      <BaseNavOption
        id="nav-exit-version-history"
        icon="icon-sign-in"
        text={window.i18next.t('pmwjs_exit_version_history')}
        optionType={params.xxsmall || params.xsmall ? NavRightSideOptionTypes.HIDDEN : NavRightSideOptionTypes.BUTTON}
        iconType={IconType.DARK_SECONDARY}
        buttonType={Type.DARK_SECONDARY}
        className="radius-8"
        onClickHandler={(): void => {
          void closeVersionHistoryPanel();
        }}
      />
    );
  };

  const getRightSideOptions = (): ReactElement<any> | null => {
    switch (mode) {
      case PosterEditingNavbarMode.EMBEDDED:
        return getRightOptionsWhenInEmailMaker();
      case PosterEditingNavbarMode.REGEN:
        return getRegenRightSideOptions();
      case PosterEditingNavbarMode.VERSION_HISTORY:
        return getVersionHistoryRightSideOptions();
      case PosterEditingNavbarMode.DEFAULT:
      default:
        return getDefaultRightSideOptions();
    }
  };

  return useMemo(() => {
    return (
      <nav ref={containerRef} className={`${styles.nav} ${isSuperUserLoggedInAsAnotherUser() ? styles.superUserLoggedInAsAnotherUser : ''} flex-justify-between flex-items-center`}>
        <NavLeftOptions
          hasSmallLogoMargin={params.smaller}
          isEmbedded={mode === PosterEditingNavbarMode.EMBEDDED}
          showBackButton={showBackButton()}
          showLogo={showLogo()}
          showFileDropdown={showFileDropdown()}
          showResize={showResizeOption()}
          isVersionHistory={mode === PosterEditingNavbarMode.VERSION_HISTORY}
        />
        {getRightSideOptions()}
      </nav>
    );
  }, [params, mode]);
}
